import { z } from 'zod';

import type { CommandFE, CommandResponseFE } from '../command';
import { fromPlainEnum, safeOptional } from './restMapping';

const KNOWN_COMMANDS = ['MRM', 'RELEASE', 'START_MISSION'] as const satisfies readonly Exclude<CommandFE, 'UNKNOWN'>[];

const label = (field: string) => `commandFromRestV1.${field}`;

const WireCommandResponseObjectSchema = z.object({
    // Identity. Strict on purpose: these must throw, see the module doc comment.
    invocationId: z.string().min(1),
    vin: z.string().min(1),
    // Required: the MRM safety path reads it, so a missing value must fail rather than degrade.
    isSuccess: z.boolean(),
    // Widened from the wire enum so an unrecognised command folds to UNKNOWN in the mapper.
    command: z.string().optional(),
    errorMessage: safeOptional(z.string(), label('errorMessage')),
    receivedByVehicleAt: safeOptional(z.string(), label('receivedByVehicleAt')),
    sentToVehicleAt: safeOptional(z.string(), label('sentToVehicleAt')),
});

/** Exported for `mocks/tests/restContract.test.ts`. */
export const WireCommandResponseSchema = WireCommandResponseObjectSchema;

type WireCommandResponse = z.infer<typeof WireCommandResponseObjectSchema>;

/**
 * The confirmed `CommandResponseTO` contract from `control-center-api`. The VHS client publishes no
 * commands operation, so the `Exclude<>` guard is written against this hand-declared spec rather
 * than a generated TO: a wire-shape drift in our own schema vs. this spec fails the build.
 */
export interface CommandResponseWireSpec {
    invocationId: string;
    command: 'MRM' | 'RELEASE' | 'START_MISSION';
    vin: string;
    isSuccess: boolean;
    receivedByVehicleAt: string;
    sentToVehicleAt: string;
    errorMessage?: string;
}

type KeysExistOn<TWire, TSpec> = Exclude<keyof TWire, keyof TSpec> extends never ? true : never;

export type CommandResponseWireKeysExistOnSpec = {
    command: KeysExistOn<WireCommandResponse, CommandResponseWireSpec>;
};

/** Maps one `CommandResponseTO` body onto the domain {@link CommandResponseFE}. */
export const commandFromRestV1 = (raw: unknown): CommandResponseFE => {
    const wire = WireCommandResponseSchema.parse(raw);
    return {
        command: fromPlainEnum<CommandFE>(wire.command, KNOWN_COMMANDS),
        vin: wire.vin,
        invocationId: wire.invocationId,
        isSuccess: wire.isSuccess,
        errorMessage: wire.errorMessage ?? undefined,
        receivedByVehicleAt: wire.receivedByVehicleAt ?? '',
        sentToVehicleAt: wire.sentToVehicleAt ?? '',
    };
};
